import React, { useEffect, useState } from 'react';
import { NavLink, Route, Routes } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { AppShell, Text, Title } from '@mantine/core';
import {
  IconBarbell,
  IconCalendarEvent,
  IconChartBar,
  IconHome,
  IconChevronRight,
  IconLayoutSidebarLeftCollapse,
  IconMusic,
} from '@tabler/icons-react';

import logo from '@assets/images/tc-gymnasium-logo.png';
import {
  AthleteCalendar,
  AthleteHome,
  AthleteMusic,
  AthleteProgress,
  AthleteWorkout,
} from '@pages';
import { RealtimeProvider } from '@lib/realtime';
import {
  AppleMusicPlayerProvider,
  AppleMusicNowPlayingSheet,
} from '@components/Athlete/AppleMusicPlayer';

import classes from './AthleteApp.module.css';

const NAV_LINKS = [
  { to: '/', label: 'Home', icon: IconHome, end: true },
  { to: '/workout', label: 'Workout', icon: IconBarbell },
  { to: '/music', label: 'Music', icon: IconMusic },
  { to: '/progress', label: 'Progress', icon: IconChartBar },
  { to: '/calendar', label: 'Calendar', icon: IconCalendarEvent },
];

const NAV_VISIBLE_KEY = 'tt.athlete.navVisible';

function readNavVisible() {
  try {
    const stored = localStorage.getItem(NAV_VISIBLE_KEY);
    if (stored === '0') return false;
    if (stored === '1') return true;
  } catch {
    // ignore storage failures
  }
  return true;
}

export default function AthleteApp() {
  const [navVisible, setNavVisible] = useState(readNavVisible);

  useEffect(() => {
    try {
      localStorage.setItem(NAV_VISIBLE_KEY, navVisible ? '1' : '0');
    } catch {
      // ignore storage failures
    }
  }, [navVisible]);

  return (
    <RealtimeProvider enabled>
      <AppleMusicPlayerProvider>
        <AppShell
          className={classes.shell}
          style={{
            '--athlete-nav-width': navVisible ? '148px' : '0px',
          }}
          navbar={{
            width: 148,
            breakpoint: 'xs',
            collapsed: { desktop: !navVisible, mobile: !navVisible },
          }}
          padding={0}
        >
          <AppShell.Navbar className={classes.navbar} withBorder={false}>
            <div className={classes.logoWrap}>
              <img
                src={logo}
                alt="T&C's Gymnasium"
                className={classes.logo}
              />
            </div>
            <nav className={classes.navList}>
              {NAV_LINKS.map((link) => {
                const Icon = link.icon;
                return (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    end={link.end}
                    className={({ isActive }) =>
                      `${classes.navLink}${isActive ? ` ${classes.navLinkActive}` : ''}`
                    }
                  >
                    <span className={classes.navIcon}>
                      <Icon size={22} stroke={1.75} />
                    </span>
                    <span>{link.label}</span>
                  </NavLink>
                );
              })}
            </nav>
            <div className={classes.navFooter}>
              <button
                type="button"
                className={classes.navToggle}
                onClick={() => setNavVisible(false)}
                aria-label="Hide navigation"
              >
                <IconLayoutSidebarLeftCollapse size={18} stroke={2} />
                <span>Hide</span>
              </button>
              <div className={classes.slogan}>Discipline builds freedom.</div>
            </div>
          </AppShell.Navbar>
          <AppShell.Main className={classes.main}>
            {!navVisible ? (
              <button
                type="button"
                className={classes.navReveal}
                onClick={() => setNavVisible(true)}
                aria-label="Show navigation"
              >
                <IconChevronRight size={16} stroke={2.5} />
              </button>
            ) : null}
            <ErrorBoundary
              fallback={
                <div style={{ padding: '2rem' }}>
                  <Title>That's not good!</Title>
                  <Text>Something went wrong and we're going to fix it.</Text>
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<AthleteHome />} />
                <Route path="/workout" element={<AthleteWorkout />} />
                <Route path="/music" element={<AthleteMusic />} />
                <Route path="/progress" element={<AthleteProgress />} />
                <Route path="/calendar" element={<AthleteCalendar />} />
                <Route path="*" element={<AthleteHome />} />
              </Routes>
              <AppleMusicNowPlayingSheet />
            </ErrorBoundary>
          </AppShell.Main>
        </AppShell>
      </AppleMusicPlayerProvider>
    </RealtimeProvider>
  );
}
